import { useEffect, useState } from 'react';
import { AlertTriangle, UserCheck, X } from 'lucide-react';
import { candidateAllocationAPI } from '../api/client';

const readableError = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((item) => item.msg).join(' ');
  return fallback;
};

export default function CandidateAllocationModal({ isOpen, onClose, match, jd, onSuccess }) {
  const [recruiters, setRecruiters] = useState([]);
  const [recruiterId, setRecruiterId] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !match) return;
    setReason('');
    setError('');
    setRecruiterId(match.allocation?.recruiter?.id ? String(match.allocation.recruiter.id) : '');
    setLoading(true);
    candidateAllocationAPI.getEligibleRecruiters(match.candidate_id)
      .then((response) => setRecruiters(response.data.recruiters || []))
      .catch((requestError) => setError(readableError(requestError, 'Could not load eligible recruiters.')))
      .finally(() => setLoading(false));
  }, [isOpen, match]);

  if (!isOpen || !match || !jd) return null;

  const validate = () => {
    if (!recruiterId) return 'Select a recruiter.';
    if (reason.trim().length < 3) return 'Enter a short operational reason.';
    return '';
  };

  const submitAllocation = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    setError('');
    try {
      await candidateAllocationAPI.direct({
        candidate_id: match.candidate_id,
        jd_id: jd.id,
        recruiter_id: Number(recruiterId),
        reason: reason.trim(),
        expected_version: match.allocation?.version || null,
      });
      await onSuccess?.();
      onClose();
    } catch (requestError) {
      setError(readableError(requestError, 'Allocation could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  const revokeAllocation = async () => {
    if (reason.trim().length < 3) {
      setError('Enter a short revocation reason.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await candidateAllocationAPI.revoke(match.allocation.id, {
        reason: reason.trim(),
        expected_version: match.allocation.version,
      });
      await onSuccess?.();
      onClose();
    } catch (requestError) {
      setError(readableError(requestError, 'Allocation could not be revoked.'));
    } finally {
      setSaving(false);
    }
  };

  const isActive = match.allocation?.status === 'active';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={(event) => event.target === event.currentTarget && onClose()}>
      <div className="w-full max-w-lg rounded-xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b p-5">
          <div>
            <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <UserCheck className="h-5 w-5 text-[#14b8a6]" />
              {isActive ? 'Reassign candidate for this JD' : 'Allocate candidate for this JD'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {match.candidate_reference} · {jd.role_title}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-100" aria-label="Close allocation dialog">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 p-5">
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
            Primary ownership remains with {match.primary_owner?.name || 'the current owner'}. This action grants responsibility and contact rights only for this JD.
          </div>

          {(match.warnings || []).map((warning) => (
            <div key={warning.type} className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{warning.message}</span>
            </div>
          ))}

          {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

          <div>
            <label htmlFor="allocation-recruiter" className="mb-1 block text-sm font-medium text-gray-700">Responsible recruiter</label>
            <select
              id="allocation-recruiter"
              value={recruiterId}
              onChange={(event) => setRecruiterId(event.target.value)}
              disabled={loading || saving}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:border-[#14b8a6] focus:outline-none focus:ring-2 focus:ring-[#14b8a6]/20"
            >
              <option value="">{loading ? 'Loading recruiters…' : 'Select recruiter'}</option>
              {recruiters.map((recruiter) => (
                <option key={recruiter.id} value={recruiter.id}>{recruiter.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="allocation-reason" className="mb-1 block text-sm font-medium text-gray-700">Operational reason</label>
            <textarea
              id="allocation-reason"
              rows={3}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              disabled={saving}
              maxLength={500}
              placeholder="Why is this recruiter being assigned?"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:border-[#14b8a6] focus:outline-none focus:ring-2 focus:ring-[#14b8a6]/20"
            />
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2 border-t p-5 sm:flex-row sm:justify-between">
          {isActive ? (
            <button type="button" onClick={revokeAllocation} disabled={saving} className="rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50">
              Revoke allocation
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button type="button" onClick={onClose} disabled={saving} className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 sm:flex-none">Cancel</button>
            <button type="button" onClick={submitAllocation} disabled={saving || loading} className="flex-1 rounded-lg bg-[#14b8a6] px-4 py-2 text-sm font-medium text-white hover:bg-[#0f766e] disabled:opacity-50 sm:flex-none">
              {saving ? 'Saving…' : isActive ? 'Save reassignment' : 'Allocate'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
